import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { plyxApi, setPlyxToken, getPlyxToken } from "@/lib/plyxApi";

const PlyxAuthContext = createContext(null);

/**
 * Plyx auth context — totally separate from AfriMarket auth.
 * Tokens live under localStorage["plyx_token"].
 */
export const PlyxAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const tok = getPlyxToken();
    if (!tok) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await plyxApi.get("/plyx/auth/me");
      setUser(data);
    } catch {
      setPlyxToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (identifier, password) => {
    const { data } = await plyxApi.post("/plyx/auth/login", { identifier, password });
    setPlyxToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (form) => {
    const { data } = await plyxApi.post("/plyx/auth/register", form);
    setPlyxToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    setPlyxToken(null);
    setUser(null);
  };

  return (
    <PlyxAuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </PlyxAuthContext.Provider>
  );
};

export const usePlyxAuth = () => {
  const ctx = useContext(PlyxAuthContext);
  if (!ctx) throw new Error("usePlyxAuth must be used inside <PlyxAuthProvider>");
  return ctx;
};
